// Browser-local discovery state. No presentation content or server preferences live here.
export const PRESENTATION_PROMOTION_DISMISSAL_MS = 30 * 24 * 60 * 60 * 1000;
export type PresentationPromotionSource =
  | 'sidebar'
  | 'presentation_creation'
  | 'inbox'
  | 'signup_presentation_landing';
const CHANGE_EVENT = 'presentation-promotion-changed';
const fallback = new Map<string, string>();
const failedWrites = new Set<string>();

export function promotionKey(userId: string, field: string) {
  return `presentation-promotion:v1:${encodeURIComponent(userId)}:${field}`;
}

export function readPromotionValue(key: string): string | null {
  if (typeof window === 'undefined') return null;
  if (failedWrites.has(key)) return fallback.get(key) ?? null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return fallback.get(key) ?? null;
  }
}

export function writePromotionValue(key: string, value: string) {
  if (typeof window === 'undefined') return;
  fallback.set(key, value);
  try {
    window.localStorage.setItem(key, value);
    failedWrites.delete(key);
  } catch {
    // Still remember within this page when browser storage is unavailable.
    failedWrites.add(key);
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function subscribeToPromotion(listener: () => void) {
  const onStorage = (event: StorageEvent) => {
    try {
      if (event.storageArea !== window.localStorage) return;
    } catch {
      // This tab may no longer have access to storage.
      return;
    }
    // A later change from another tab supersedes this page's in-memory fallback.
    if (event.key === null) {
      fallback.clear();
      failedWrites.clear();
    } else {
      fallback.delete(event.key);
      failedWrites.delete(event.key);
    }
    listener();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(CHANGE_EVENT, listener);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(CHANGE_EVENT, listener);
  };
}

export function markPresentationTemplateUsed(userId: string) {
  const key = promotionKey(userId, 'used');
  if (readPromotionValue(key) !== 'true') writePromotionValue(key, 'true');
}

export function dismissPresentationPromotion(userId: string, source: PresentationPromotionSource) {
  writePromotionValue(
    promotionKey(userId, `dismissed-until:${source}`),
    String(Date.now() + PRESENTATION_PROMOTION_DISMISSAL_MS)
  );
}

export function claimPresentationPromotion(userId: string, conversationId: string) {
  const key = promotionKey(userId, `chat:${conversationId}`);
  if (readPromotionValue(key)) return false;
  writePromotionValue(key, 'true');
  return true;
}

export function isPresentationSkillCall(call?: { name?: string; arguments?: unknown }) {
  if (call?.name !== 'fetch_skill') return false;
  try {
    const args = typeof call.arguments === 'string' ? JSON.parse(call.arguments) : call.arguments;
    return Array.isArray(args?.titles) && args.titles.includes('create-slides-pptx');
  } catch {
    // Partial streaming arguments aren't a skill selection yet.
    return false;
  }
}

// Only a live turn can start a promotion; historical slide tools must not revive it.
export function isPresentationCreationTurn(
  groups: readonly {
    is_last_group?: boolean;
    message_list?: readonly {
      turn_id?: string;
      function_call?: { name?: string; arguments?: unknown };
    }[];
  }[],
  isTurnActive: boolean,
  activeTurnId?: string
) {
  return (
    isTurnActive &&
    groups.some((group) =>
      group.message_list?.some(
        (message) =>
          ((!!activeTurnId && message.turn_id === activeTurnId) ||
            (!message.turn_id && group.is_last_group)) &&
          isPresentationSkillCall(message.function_call)
      )
    )
  );
}
